/* -----------------Globals--------------- */
import { useLocalSearchParams, useRouter } from "expo-router";

/* -----------------UI--------------- */
import { Appbar } from "react-native-paper";

import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import { pageViewTypes } from "@constants/tracking";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
import { useDevice } from "@hooks/useDevice";
import { useProductDetails } from "@hooks/useProduct";
import {
	calculatePlanExpiry,
	getDurationText,
} from "@memorang/helpers/src/bundle";
import type { ProductType } from "@memorang/types/src/bundle";
import Box from "@memorang/ui/src/components/Box";
import Loader from "@memorang/ui/src/components/Loader";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
/* -----------------Child components--------------- */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchExamDetails } from "api/commerce/exam-service";
import { AccessGrantedView } from "components/AccessGrantedView";
import CustomAppbar from "components/CustomAppbar";
import DocumentTitle from "components/DocumentTitle";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
const SuccessRoute = () => {
	const { productType } = useLocalSearchParams<{
		productType: ProductType;
	}>();

	const router = useRouter();

	const {
		duration,
		interval,
		amount,
		currency,
		planId,
		id: productId,
	} = useLocalSearchParams<{
		duration: string;
		interval: string;
		amount: string;
		currency: string;
		planId: string;
		id: string;
	}>();

	const { data: productDetails } = useProductDetails(
		productType,
		Number(productId),
	);

	const examId = productDetails?.examUUID;

	const { data: examDetails, isLoading: isLoadingExamDetails } = useQuery({
		queryKey: [cacheKeys.examDetails, examId],
		queryFn: async () => {
			if (!examId) {
				return undefined;
			}
			return await fetchExamDetails({ examId });
		},
		enabled: !!examId,
	});

	const { explicitCallTrackCustomEvent } = useAnalytics({
		type: pageViewTypes.purchaseSuccess,
	});

	const { isMobile } = useDevice();
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const headerTitle = t("purchase.success.headerTitle");

	useEffect(() => {
		if (productDetails) {
			const bundleType = productDetails.bundleType;
			explicitCallTrackCustomEvent({
				eventName: events.paymentSuccessful,
				product_id: productDetails.numericId,
				product_name: productDetails.name,
				plan_amount: amount,
				plan_currency: currency,
				plan_id: planId,
				product_type: bundleType,
			});
			queryClient.invalidateQueries();
		}
	}, [
		productDetails,
		queryClient,
		explicitCallTrackCustomEvent,
		amount,
		currency,
		planId,
	]);

	if (!productDetails || isLoadingExamDetails) {
		return (
			<Box flex={1} height="100%" justifyContent="center">
				<CustomAppbar
					options={{
						headerTitle,
						subtitle: isMobile ? "" : t("purchase.success.subtitle"),
						headerLeft: () => (
							<Appbar.BackAction
								onPress={() => router.replace("/(protected)/(tabs)/home")}
							/>
						),
						headerRight: () => null,
					}}
				/>
				<Loader />
			</Box>
		);
	}

	const planExpiry = duration
		? calculatePlanExpiry(interval, Number(duration))
		: undefined;
	const expirationDateText = duration
		? getDurationText(Number(duration), interval)
		: undefined;

	const {
		name,
		examName,
		logicalId: id,
		icon: { url },
		features,
		bundleType,
		bundleSubType,
	} = productDetails;

	const title = t("purchase.success.accessGranted", { name });
	const subtitle = isMobile
		? ""
		: t("purchase.success.purchaseSuccessful", { name });

	const products = [
		{
			id,
			title: name,
			description:
				bundleType === "STUDY_PACK" || bundleSubType === "STUDY_PACK"
					? t("purchase.success.studyPack")
					: t("purchase.success.practiceTest"),
			icon: url,
			features,
			isCustomAccess: false,
		},
	];
	const successfulPurchaseTitle = `Congratulations! You have unlocked **${expirationDateText}** access to the **${name}**. This will expire on **${planExpiry}**.`;

	return (
		<>
			<CustomAppbar
				options={{
					headerTitle,
					subtitle,
					headerRight: () => null,
					headerLeft: () => (
						<Appbar.BackAction
							onPress={() => {
								//Not sure but for some reason the router.replace is not working on mobile
								if (isWeb) {
									router.replace("/(protected)/(tabs)/home");
								} else {
									router.back();
								}
							}}
						/>
					),
				}}
			/>
			<DocumentTitle title={t("purchase.success.documentTitle")} />
			<ScrollContainer maxWidth="xs">
				<Box paddingTop={24} paddingBottom={44}>
					<AccessGrantedView
						products={products}
						examName={examName}
						title={title}
						examDetails={examDetails}
						successfulPurchaseTitle={successfulPurchaseTitle}
					/>
				</Box>
			</ScrollContainer>
		</>
	);
};

export default SuccessRoute;
